import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { entero, porcentaje } from '../lib/formato.js';
import { txt, TXT_CTR_ESTADO, TXT_PRI } from '../lib/enums.js';
import { Pagehead } from '../piezas/Cascaron.jsx';
import { Cargando, Error as AvisoError, Vacio } from '../piezas/Estados.jsx';
import { useSesion } from '../piezas/Sesion.jsx';

/**
 * Analitica del periodo.
 *
 * Todas las graficas de esta pantalla son de UNA sola serie, y es una
 * decision, no una limitacion.
 *
 * El primer diseno ponia cumplimiento de respuesta y de solucion como dos
 * lineas en la misma grafica. Al validar la pareja de colores contra los
 * tokens de Enteratek, las dos variantes accesibles quedaban en ΔE 12.6 en
 * vision normal —por debajo del umbral de 15— asi que ni siquiera un lector
 * con vision completa las distinguiria con seguridad. Buscar una pareja que
 * pasara en los DOS temas no dio ninguna dentro de la paleta de la marca.
 *
 * Lo que resolvio el problema no fue el color: fue notar que la segunda serie
 * no aportaba nada. El cumplimiento de solucion ronda el 99% y es plano, asi
 * que su lugar es una cifra, no una linea compitiendo por atencion. Con una
 * sola serie no hace falta leyenda —el titulo nombra la serie— ni pareja
 * categorica que validar.
 *
 * El color que SI se usa es de estado —cumple, en riesgo, incumple— y va
 * siempre acompanado del numero escrito, nunca solo. Y la serie tiene vista
 * de tabla, que es lo que exige un contraste de 2.56:1 del color de marca
 * sobre fondo claro y lo que de todos modos quiere quien arma un reporte.
 */

interface Mes {
  mes: string;
  tickets: number;
  cerrados: number;
  visitas_falsas: number;
  respuesta_ok: number;
  respuesta_medibles: number;
  solucion_ok: number;
  solucion_medibles: number;
  horas_respuesta: number | null;
  horas_solucion: number | null;
}

interface Corte {
  tickets: number;
  respuesta_ok: number;
  respuesta_medibles: number;
  horas_respuesta?: number | null;
}

interface Analitica {
  meses: number;
  serie: Mes[];
  por_cliente: (Corte & { cliente: string })[];
  por_marca: (Corte & { cliente: string; marca: string })[];
  por_prioridad: (Corte & { prioridad: string })[];
  por_servicio: (Corte & { servicio: string })[];
  por_causal: {
    causal: string;
    imputable_a: string | null;
    consume_saldo: boolean;
    tickets: number;
  }[];
  por_idc: (Corte & { idc: string; ciudad: string | null })[];
  contratos: {
    cliente: string;
    folio: string;
    estado: string;
    inicio_el: string;
    fin_el: string;
    volumen: number;
    consumido: number;
    disponible: number;
    agotado: boolean;
    moneda?: string;
  }[];
  compromisos: {
    cliente: string;
    contrato: string;
    servicio: string;
    prioridad: string;
    horas_respuesta: number;
    horas_solucion: number;
    reloj: string;
    dias_habiles: number[];
  }[];
}

/** El objetivo contra el que se juzga. Se dibuja para no juzgar a ojo. */
const META = 95;

function pct(ok: number, medibles: number): number | null {
  return medibles > 0 ? (ok / medibles) * 100 : null;
}

/** Estado de un cumplimiento. El color acompana al numero, nunca lo sustituye. */
function estadoDe(valor: number | null): { clase: string; etiqueta: string; color: string } {
  if (valor === null) return { clase: 't-mute', etiqueta: 'sin datos', color: 'var(--txt-3-ac)' };
  if (valor >= META) return { clase: 't-ok', etiqueta: 'cumple', color: 'var(--ok-ac)' };
  if (valor >= META - 7) return { clase: 't-warm', etiqueta: 'en riesgo', color: 'var(--warn)' };
  return { clase: 't-hot', etiqueta: 'incumple', color: 'var(--bad-ac)' };
}

const MES_CORTO = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
];

function etiquetaMes(mes: string): string {
  const [anio, m] = mes.split('-');
  return `${MES_CORTO[Number(m) - 1] ?? m} ${anio?.slice(2) ?? ''}`;
}

const DIAS = ['', 'L', 'M', 'M', 'J', 'V', 'S', 'D'];

export function Analitica(): JSX.Element {
  const [d, setD] = useState<Analitica | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [meses, setMeses] = useState(24);
  const [tabla, setTabla] = useState(false);
  const { veMontos } = useSesion();

  useEffect(() => {
    setD(null);
    setError(null);
    api
      .obtener<Analitica>(`/v1/analitica?meses=${meses}`)
      .then(({ datos }) => setD(datos))
      .catch(setError);
  }, [meses]);

  const resumen = useMemo(() => {
    if (!d) return null;
    const s = d.serie.reduce(
      (a, m) => ({
        tickets: a.tickets + m.tickets,
        respuestaOk: a.respuestaOk + m.respuesta_ok,
        respuestaMed: a.respuestaMed + m.respuesta_medibles,
        solucionOk: a.solucionOk + m.solucion_ok,
        solucionMed: a.solucionMed + m.solucion_medibles,
        falsas: a.falsas + m.visitas_falsas,
      }),
      { tickets: 0, respuestaOk: 0, respuestaMed: 0, solucionOk: 0, solucionMed: 0, falsas: 0 },
    );
    const conHoras = d.serie.filter((m) => m.horas_respuesta !== null);
    const horas =
      conHoras.length > 0
        ? conHoras.reduce((a, m) => a + (m.horas_respuesta ?? 0) * m.tickets, 0) /
          conHoras.reduce((a, m) => a + m.tickets, 0)
        : null;
    return {
      ...s,
      respuesta: pct(s.respuestaOk, s.respuestaMed),
      solucion: pct(s.solucionOk, s.solucionMed),
      horas,
    };
  }, [d]);

  if (error !== null) {
    return (
      <>
        <Pagehead />
        <AvisoError error={error} />
      </>
    );
  }
  if (!d || !resumen) {
    return (
      <>
        <Pagehead />
        <div className="card">
          <Cargando que="la analitica del periodo" />
        </div>
      </>
    );
  }

  const est = estadoDe(resumen.respuesta);

  return (
    <>
      <Pagehead />

      <div className="fila wrap" style={{ marginBottom: 14 }}>
        <div className="chips" role="group" aria-label="Periodo">
          {[6, 12, 24].map((m) => (
            <button
              key={m}
              type="button"
              className={meses === m ? 'chip on' : 'chip'}
              onClick={() => setMeses(m)}
              data-prueba={`periodo-${m}`}
            >
              {m} meses
            </button>
          ))}
        </div>
        <span className="crece" />
        <button
          type="button"
          className="btn sm"
          aria-pressed={tabla}
          onClick={() => setTabla(!tabla)}
          data-prueba="ver-tabla"
        >
          {tabla ? '▦ Ver grafica' : '☰ Ver tabla'}
        </button>
      </div>

      {/* --------------------------- Cifras ---------------------------- */}
      <div className="grid kpis" style={{ marginBottom: 14 }}>
        <div className="card kpi">
          <span className="ico" aria-hidden="true">
            ⏱
          </span>
          <div className="val">
            <b style={{ fontSize: 22 }} data-prueba="sla-respuesta">
              {resumen.respuesta === null
                ? '—'
                : porcentaje(Math.round(resumen.respuesta * 10) / 10)}
            </b>
            <span className="muted">SLA de respuesta</span>
            <span className={`tag ${est.clase}`} style={{ marginTop: 4 }}>
              {est.etiqueta} · meta {META}%
            </span>
          </div>
        </div>
        <div className="card kpi">
          <span className="ico" aria-hidden="true">
            ✔
          </span>
          <div className="val">
            <b style={{ fontSize: 22 }}>
              {resumen.solucion === null ? '—' : porcentaje(Math.round(resumen.solucion * 10) / 10)}
            </b>
            <span className="muted">SLA de solucion</span>
          </div>
        </div>
        <div className="card kpi">
          <span className="ico" aria-hidden="true">
            🎫
          </span>
          <div className="val">
            <b style={{ fontSize: 22 }}>{entero(resumen.tickets)}</b>
            <span className="muted">servicios en el periodo</span>
          </div>
        </div>
        <div className="card kpi">
          <span className="ico" aria-hidden="true">
            🕐
          </span>
          <div className="val">
            <b style={{ fontSize: 22 }}>
              {resumen.horas === null
                ? '—'
                : `${(Math.round(resumen.horas * 10) / 10).toFixed(1)} h`}
            </b>
            {/* Horas HABILES, no corridas. El mosaico decia "respuesta
                promedio" sobre horas de reloj y quedaba al lado del
                cumplimiento, que se mide contra la ventana contratada: 26.5 h
                junto a una meta de 4 h se lee como un desastre que no existe.
                Medido con el mismo reloj de la meta, son 10.4 h. */}
            <span className="muted">respuesta promedio (horas habiles)</span>
          </div>
        </div>
        <div className="card kpi">
          <span className="ico" aria-hidden="true">
            🚪
          </span>
          <div className="val">
            <b style={{ fontSize: 22 }}>{entero(resumen.falsas)}</b>
            <span className="muted">visitas en falso</span>
          </div>
        </div>
      </div>

      {/* --------------------- Serie mensual de SLA --------------------- */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-h">
          <h2>Cumplimiento de SLA de respuesta, mes por mes</h2>
        </div>
        {tabla ? <TablaSerie serie={d.serie} /> : <SerieSla serie={d.serie} />}
      </div>

      {/* ------------------------ Volumen mensual ---------------------- */}
      {/* Grafica APARTE y no un segundo eje en la de arriba: dos escalas
          distintas en un mismo marco es la forma mas comun de hacer que una
          grafica diga lo que uno quiera. */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-h">
          <h2>Servicios levantados por mes</h2>
        </div>
        <SerieVolumen serie={d.serie} />
      </div>

      <div className="grid r2">
        <Corte
          titulo="Por cuenta"
          filas={d.por_cliente.map((x) => ({ etiqueta: x.cliente, ...x }))}
        />
        <Corte
          titulo="Por prioridad"
          filas={d.por_prioridad.map((x) => ({
            etiqueta: txt(TXT_PRI, x.prioridad),
            ...x,
          }))}
        />
      </div>

      {d.por_marca.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <Corte
            titulo="Por marca"
            filas={d.por_marca.map((x) => ({ etiqueta: x.marca, nota: x.cliente, ...x }))}
          />
        </div>
      )}

      <div className="grid r2" style={{ marginTop: 14 }}>
        <Corte
          titulo="Por tipo de servicio"
          filas={d.por_servicio.map((x) => ({ etiqueta: x.servicio, ...x }))}
        />
        <Corte
          titulo="Por ingeniero"
          filas={d.por_idc.slice(0, 10).map((x) => ({
            etiqueta: x.idc,
            nota: x.ciudad ?? undefined,
            ...x,
          }))}
          ayuda="Solo ingenieros con 30 servicios o mas: con menos, un incumplimiento es un porcentaje que no dice nada de nadie."
        />
      </div>

      {/* ------------------- Causales imputables ----------------------- */}
      <div className="card p0" style={{ marginTop: 14 }}>
        <div style={{ padding: '14px 16px 0' }}>
          <div className="sec-title">Causales de cierre sin servicio</div>
        </div>
        {d.por_causal.length === 0 ? (
          <Vacio mensaje="Ningun cierre con causal en el periodo" icono="⚖" />
        ) : (
          <table data-prueba="tabla-causales">
            <thead>
              <tr>
                <th>Causal</th>
                <th>Imputable a</th>
                <th>Consume contrato</th>
                <th className="num">Servicios</th>
              </tr>
            </thead>
            <tbody>
              {d.por_causal.map((c) => (
                <tr key={c.causal}>
                  <td>{c.causal}</td>
                  <td>{c.imputable_a ?? <span className="muted">—</span>}</td>
                  <td>
                    {c.consume_saldo ? (
                      <span className="tag t-hot">si, gasta contrato</span>
                    ) : (
                      <span className="tag t-ok">no lo gasta</span>
                    )}
                  </td>
                  <td className="num">{entero(c.tickets)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* -------------------------- Contratos -------------------------- */}
      <div className="card p0" style={{ marginTop: 14 }}>
        <div style={{ padding: '14px 16px 0' }}>
          <div className="sec-title">Contratos del periodo</div>
        </div>
        <table data-prueba="tabla-contratos-analitica">
          <thead>
            <tr>
              <th>Cuenta</th>
              <th>Folio</th>
              <th>Vigencia</th>
              <th>Estado</th>
              <th className="num">Volumen</th>
              <th className="num">Consumido</th>
              <th>Saldo</th>
            </tr>
          </thead>
          <tbody>
            {d.contratos.map((c) => {
              const consumo = c.volumen > 0 ? (c.consumido / c.volumen) * 100 : 0;
              return (
                <tr key={c.folio}>
                  <td>{c.cliente}</td>
                  <td className="folio">{c.folio}</td>
                  <td className="sec">
                    {c.inicio_el.slice(0, 7)} a {c.fin_el.slice(0, 7)}
                  </td>
                  <td>
                    <span className={c.estado === 'CTR_VIGENTE' ? 'tag t-ok' : 'tag t-mute'}>
                      {txt(TXT_CTR_ESTADO, c.estado)}
                    </span>
                  </td>
                  <td className="num">{entero(c.volumen)}</td>
                  <td className="num">{entero(c.consumido)}</td>
                  <td>
                    <div className="fila" style={{ gap: 6, fontSize: 12 }}>
                      {c.disponible < 0 ? (
                        <span className="tag t-hot">excedido en {entero(-c.disponible)}</span>
                      ) : c.agotado ? (
                        <span className="tag t-hot">agotado</span>
                      ) : (
                        <span>{entero(c.disponible)} disponibles</span>
                      )}
                    </div>
                    <div className="bar" style={{ marginTop: 4, minWidth: 90 }}>
                      <i
                        style={{
                          width: `${Math.min(consumo, 100)}%`,
                          background: consumo >= 100 ? 'var(--bad)' : 'var(--ok)',
                        }}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ---------------------- SLA contratado ------------------------- */}
      {/* Va en la misma pantalla a proposito: un 84% no se juzga igual si el
          compromiso son cuatro horas o cuarenta, y abrir otra pantalla para
          saberlo es como no tenerlo. */}
      <div className="card p0" style={{ marginTop: 14 }}>
        <div style={{ padding: '14px 16px 0' }}>
          <div className="sec-title">SLA contratado y vigente</div>
        </div>
        <table data-prueba="tabla-sla">
          <thead>
            <tr>
              <th>Cuenta</th>
              <th>Servicio</th>
              <th>Prioridad</th>
              <th className="num">Respuesta</th>
              <th className="num">Solucion</th>
              <th>Reloj</th>
            </tr>
          </thead>
          <tbody>
            {d.compromisos.map((s, i) => (
              <tr key={`${s.contrato}-${s.servicio}-${s.prioridad}-${i}`}>
                <td>{s.cliente}</td>
                <td>{s.servicio}</td>
                <td>{txt(TXT_PRI, s.prioridad)}</td>
                <td className="num">{s.horas_respuesta} h</td>
                <td className="num">{s.horas_solucion} h</td>
                <td>
                  {s.reloj === 'RELOJ_24X7' ? (
                    <span className="tag t-hot">continuo 24x7</span>
                  ) : (
                    <>
                      <span className="tag t-cold">ventana</span>
                      <span className="muted mono" style={{ marginLeft: 6, fontSize: 11 }}>
                        {s.dias_habiles.map((n) => DIAS[n] ?? '').join('')}
                      </span>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!veMontos && (
        <p className="muted" style={{ fontSize: 11.5, marginTop: 12 }}>
          Tu perfil no recibe informacion economica, asi que este reporte no incluye valores.
        </p>
      )}
    </>
  );
}

/* ======================= Serie de cumplimiento ======================= */

/**
 * Una sola serie, dibujada como columnas con la meta como linea de
 * referencia. Columnas y no linea porque cada mes es una medicion
 * independiente de un conjunto distinto de tickets, no una magnitud que fluye.
 */
function SerieSla({ serie }: { serie: Mes[] }): JSX.Element {
  const ancho = 1000;
  const alto = 260;
  const margen = { arriba: 16, derecha: 16, abajo: 34, izquierda: 42 };
  const util = {
    w: ancho - margen.izquierda - margen.derecha,
    h: alto - margen.arriba - margen.abajo,
  };
  const conDatos = serie.filter((m) => m.respuesta_medibles > 0);

  if (conDatos.length === 0)
    return <Vacio mensaje="Sin servicios medibles en el periodo" icono="📈" />;

  /* El eje arranca en 60 y no en 0: entre 85% y 95% esta toda la diferencia
     que importa, y con el cero adentro esa diferencia es un pixel. Se dice en
     el eje para que nadie lea la altura como proporcional. */
  const minimo = 60;
  const y = (v: number): number =>
    margen.arriba + util.h - ((Math.max(v, minimo) - minimo) / (100 - minimo)) * util.h;
  const paso = util.w / serie.length;
  const anchoBarra = Math.min(paso * 0.62, 26);

  return (
    <>
      <svg
        viewBox={`0 0 ${ancho} ${alto}`}
        style={{ width: '100%', height: 'auto' }}
        role="img"
        aria-label={`Cumplimiento mensual de SLA de respuesta en ${conDatos.length} meses, meta ${META} por ciento`}
      >
        {[60, 70, 80, 90, 100].map((v) => (
          <g key={v}>
            <line
              x1={margen.izquierda}
              x2={ancho - margen.derecha}
              y1={y(v)}
              y2={y(v)}
              stroke="var(--line)"
              strokeWidth="1"
            />
            <text
              x={margen.izquierda - 8}
              y={y(v) + 4}
              textAnchor="end"
              fontSize="10.5"
              fill="var(--txt-3-ac)"
            >
              {v}%
            </text>
          </g>
        ))}

        {/* La meta, como referencia visible y nombrada. */}
        <line
          x1={margen.izquierda}
          x2={ancho - margen.derecha}
          y1={y(META)}
          y2={y(META)}
          stroke="var(--brand-ac)"
          strokeWidth="2"
          strokeDasharray="6 4"
        />
        <text
          x={ancho - margen.derecha}
          y={y(META) - 6}
          textAnchor="end"
          fontSize="10.5"
          fill="var(--brand-ac)"
        >
          meta {META}%
        </text>

        {serie.map((m, i) => {
          const v = pct(m.respuesta_ok, m.respuesta_medibles);
          if (v === null) return null;
          const cx = margen.izquierda + paso * i + paso / 2;
          const est = estadoDe(v);
          const alturaBarra = margen.arriba + util.h - y(v);
          return (
            <g key={m.mes}>
              <title>{`${etiquetaMes(m.mes)}: ${v.toFixed(1)}% de ${m.respuesta_medibles} servicios`}</title>
              <rect
                x={cx - anchoBarra / 2}
                y={y(v)}
                width={anchoBarra}
                height={Math.max(alturaBarra, 2)}
                rx="3"
                fill={est.color}
              />
              {/* Etiqueta directa cada tres meses: un numero en cada columna
                  es ruido, y ninguno obliga a medir con el ojo. */}
              {i % 3 === 0 && (
                <text x={cx} y={y(v) - 6} textAnchor="middle" fontSize="10" fill="var(--txt-2-ac)">
                  {Math.round(v)}
                </text>
              )}
              {i % 3 === 0 && (
                <text x={cx} y={alto - 12} textAnchor="middle" fontSize="10" fill="var(--txt-3-ac)">
                  {etiquetaMes(m.mes)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <p className="muted" style={{ fontSize: 11, marginTop: 4 }}>
        El eje vertical arranca en 60% para que la diferencia entre 85 y 95 se vea: la altura de la
        columna no es proporcional al valor.
      </p>
    </>
  );
}

function SerieVolumen({ serie }: { serie: Mes[] }): JSX.Element {
  const maximo = Math.max(...serie.map((m) => m.tickets), 1);
  const ancho = 1000;
  const alto = 150;
  const margen = { arriba: 10, derecha: 16, abajo: 28, izquierda: 42 };
  const util = {
    w: ancho - margen.izquierda - margen.derecha,
    h: alto - margen.arriba - margen.abajo,
  };
  const paso = util.w / serie.length;
  const anchoBarra = Math.min(paso * 0.62, 26);

  return (
    <svg
      viewBox={`0 0 ${ancho} ${alto}`}
      style={{ width: '100%', height: 'auto' }}
      role="img"
      aria-label={`Servicios levantados por mes, maximo ${maximo}`}
    >
      <line
        x1={margen.izquierda}
        x2={ancho - margen.derecha}
        y1={margen.arriba + util.h}
        y2={margen.arriba + util.h}
        stroke="var(--line)"
      />
      <text
        x={margen.izquierda - 8}
        y={margen.arriba + 10}
        textAnchor="end"
        fontSize="10.5"
        fill="var(--txt-3-ac)"
      >
        {entero(maximo)}
      </text>
      {serie.map((m, i) => {
        const h = (m.tickets / maximo) * util.h;
        const cx = margen.izquierda + paso * i + paso / 2;
        return (
          <g key={m.mes}>
            <title>{`${etiquetaMes(m.mes)}: ${m.tickets} servicios`}</title>
            <rect
              x={cx - anchoBarra / 2}
              y={margen.arriba + util.h - h}
              width={anchoBarra}
              height={Math.max(h, 1)}
              rx="3"
              fill="var(--info-ac)"
            />
            {i % 3 === 0 && (
              <text x={cx} y={alto - 10} textAnchor="middle" fontSize="10" fill="var(--txt-3-ac)">
                {etiquetaMes(m.mes)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function TablaSerie({ serie }: { serie: Mes[] }): JSX.Element {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table data-prueba="tabla-serie">
        <thead>
          <tr>
            <th>Mes</th>
            <th className="num">Servicios</th>
            <th className="num">Cerrados</th>
            <th className="num">En falso</th>
            <th className="num">SLA respuesta</th>
            <th className="num">SLA solucion</th>
            <th className="num">Horas respuesta (habiles)</th>
          </tr>
        </thead>
        <tbody>
          {serie.map((m) => {
            const r = pct(m.respuesta_ok, m.respuesta_medibles);
            const s = pct(m.solucion_ok, m.solucion_medibles);
            return (
              <tr key={m.mes}>
                <td>{etiquetaMes(m.mes)}</td>
                <td className="num">{entero(m.tickets)}</td>
                <td className="num">{entero(m.cerrados)}</td>
                <td className="num">{entero(m.visitas_falsas)}</td>
                <td className="num">
                  {r === null ? (
                    <span className="muted">—</span>
                  ) : (
                    <span className={`tag ${estadoDe(r).clase}`}>{r.toFixed(1)}%</span>
                  )}
                </td>
                <td className="num">{s === null ? '—' : `${s.toFixed(1)}%`}</td>
                <td className="num">{m.horas_respuesta === null ? '—' : m.horas_respuesta}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ============================ Un corte ============================== */

interface FilaCorte {
  etiqueta: string;
  nota?: string | undefined;
  tickets: number;
  respuesta_ok: number;
  respuesta_medibles: number;
  horas_respuesta?: number | null;
}

/**
 * Barras horizontales de una sola serie, con el numero escrito al lado.
 *
 * El color es de estado y repite lo que ya dice el numero, no lo sustituye:
 * quien no distinga el verde del ambar lee 91.2% igual.
 */
function Corte({
  titulo,
  filas,
  ayuda,
}: {
  titulo: string;
  filas: FilaCorte[];
  ayuda?: string;
}): JSX.Element {
  if (filas.length === 0) {
    return (
      <div className="card">
        <div className="card-h">
          <h2>{titulo}</h2>
        </div>
        <Vacio mensaje="Sin datos en el periodo" icono="📊" />
      </div>
    );
  }
  return (
    <div className="card">
      <div className="card-h">
        <h2>{titulo}</h2>
      </div>
      <div className="barras">
        {filas.map((f) => {
          const v = pct(f.respuesta_ok, f.respuesta_medibles);
          const est = estadoDe(v);
          return (
            <div className="barra" key={`${titulo}-${f.etiqueta}`}>
              <div className="barra-et">
                {f.etiqueta}
                {f.nota && <span className="muted"> · {f.nota}</span>}
              </div>
              <div className="barra-pista">
                <i style={{ width: `${v ?? 0}%`, background: est.color }} />
              </div>
              <div className="barra-val">
                <b>{v === null ? '—' : `${v.toFixed(1)}%`}</b>
                <span className="muted">{entero(f.tickets)} serv.</span>
              </div>
            </div>
          );
        })}
      </div>
      {ayuda && (
        <p className="muted" style={{ fontSize: 11, marginTop: 8 }}>
          {ayuda}
        </p>
      )}
    </div>
  );
}
